"use client";

import { useEffect, useRef, useState, type RefObject } from "react";

import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

export const CC_LATCH_IN_VIEW_DEFAULT_THRESHOLD = 0.3;

export type UseCcLatchInViewOptions = {
  threshold?: number;
  rootMargin?: string;
  /** When false, the observer does not run until enabled (e.g. inactive tab). */
  enabled?: boolean;
};

/**
 * Latch-once intersection: `isInView` becomes true when the target enters the viewport
 * (or immediately when the user prefers reduced motion).
 */
export function useCcLatchInView<T extends HTMLElement = HTMLElement>({
  threshold = CC_LATCH_IN_VIEW_DEFAULT_THRESHOLD,
  rootMargin,
  enabled = true,
}: UseCcLatchInViewOptions = {}): {
  ref: RefObject<T | null>;
  isInView: boolean;
} {
  const ref = useRef<T | null>(null);
  const prefersReducedMotion = useCcPrefersReducedMotion();
  const [isInView, setIsInView] = useState(prefersReducedMotion);

  useEffect(() => {
    if (prefersReducedMotion) {
      setIsInView(true);
      return;
    }

    if (!enabled) {
      return;
    }

    const node = ref.current;
    if (!node) {
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [enabled, prefersReducedMotion, rootMargin, threshold]);

  return { ref, isInView: prefersReducedMotion || isInView };
}
